/**
 * BadgeUnlockModal - Full badge reveal with shine effect
 *
 * Features:
 * - Full badge reveal animation with shine effect
 * - Badge rarity glow (common=white, rare=blue, epic=purple, legendary=gold)
 * - Description and how it was earned
 * - Share button for Discord
 * - Click outside to dismiss
 */

import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import { cn } from '@/lib/utils';
import BadgeIconService from '@/lots/badges/logic/BadgeIconService';
import {
  Star,
  Sparkles,
  Share2,
  X,
  Trophy,
  Zap,
  Crown,
  Award,
  ExternalLink,
} from 'lucide-react';

// Rarity configurations
const RARITY_CONFIG = {
  common: {
    name: 'Common',
    color: '#FFFFFF',
    glow: 'shadow-[0_0_30px_rgba(255,255,255,0.4)]',
    ring: 'ring-gray-400/50',
    bg: 'from-gray-400/20 to-gray-500/10',
    border: 'border-gray-400/30',
    text: 'text-gray-300',
    particles: 20,
    confettiColors: ['#FFFFFF', '#E5E5E5', '#CCCCCC'],
  },
  rare: {
    name: 'Rare',
    color: '#3B82F6',
    glow: 'shadow-[0_0_40px_rgba(59,130,246,0.6)]',
    ring: 'ring-blue-500/70',
    bg: 'from-blue-500/30 to-cyan-500/20',
    border: 'border-blue-500/50',
    text: 'text-blue-300',
    particles: 40,
    confettiColors: ['#3B82F6', '#60A5FA', '#93C5FD'],
  },
  epic: {
    name: 'Epic',
    color: '#A855F7',
    glow: 'shadow-[0_0_50px_rgba(168,85,247,0.7)]',
    ring: 'ring-purple-500/80',
    bg: 'from-purple-500/40 to-pink-500/20',
    border: 'border-purple-500/60',
    text: 'text-purple-300',
    particles: 60,
    confettiColors: ['#A855F7', '#C084FC', '#E879F9'],
  },
  legendary: {
    name: 'Legendary',
    color: '#FFD700',
    glow: 'shadow-[0_0_60px_rgba(255,215,0,0.8)]',
    ring: 'ring-yellow-400/90',
    bg: 'from-yellow-500/50 to-orange-500/30',
    border: 'border-yellow-400/70',
    text: 'text-yellow-300',
    particles: 100,
    confettiColors: ['#FFD700', '#FFA500', '#FF8C00', '#FFDF00'],
  },
};

// Shine effect animation
const ShineEffect = () => (
  <motion.div
    initial={{ x: '-100%', opacity: 0 }}
    animate={{ x: '200%', opacity: [0, 1, 0] }}
    transition={{
      duration: 1.5,
      ease: 'easeInOut',
      repeat: Infinity,
      repeatDelay: 3
    }}
    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent pointer-events-none"
    style={{ transform: 'skewX(-20deg)' }}
  />
);

// Floating particles
const FloatingParticle = ({ delay, duration, scale }) => (
  <motion.div
    initial={{ y: 0, opacity: 0 }}
    animate={{
      y: [-100, -200],
      opacity: [0, 1, 0],
      x: [0, Math.random() * 40 - 20]
    }}
    transition={{
      duration,
      delay,
      repeat: Infinity,
      ease: 'easeOut'
    }}
    className="absolute"
    style={{
      left: `${Math.random() * 100}%`,
      bottom: '10%'
    }}
  >
    <Sparkles className="text-yellow-400" style={{ transform: `scale(${scale})` }} />
  </motion.div>
);

const BadgeUnlockModal = ({
  isOpen,
  onClose,
  badge,
  onShare,
}) => {
  const [showContent, setShowContent] = useState(false);

  const {
    name = 'New Badge',
    description = 'You earned this badge!',
    category = 'achievement',
    type = 'achievement',
    rarity = 'common',
    xpAwarded = 0,
    earnedReason = 'Completed a challenge',
  } = badge || {};

  const rarityConfig = RARITY_CONFIG[rarity] || RARITY_CONFIG.common;
  const IconComponent = BadgeIconService.getBadgeIcon(type, category);

  // Trigger confetti based on rarity
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        const colors = rarityConfig.confettiColors;
        const particleCount = rarityConfig.particles;

        // Initial burst
        confetti({
          particleCount,
          spread: 100,
          origin: { y: 0.5 },
          colors,
          startVelocity: 45,
          gravity: 0.8,
          scalar: 1.2,
        });

        // Legendary gets extra bursts
        if (rarity === 'legendary') {
          setTimeout(() => {
            confetti({
              particleCount: 50,
              angle: 60,
              spread: 55,
              origin: { x: 0, y: 0.6 },
              colors,
            });
            confetti({
              particleCount: 50,
              angle: 120,
              spread: 55,
              origin: { x: 1, y: 0.6 },
              colors,
            });
          }, 200);

          setTimeout(() => {
            confetti({
              particleCount: 30,
              spread: 360,
              origin: { y: 0.5 },
              colors,
              scalar: 0.8,
            });
          }, 400);
        }
      }, 100);
    }
  }, [isOpen, rarity, rarityConfig]);

  // Content animation
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setShowContent(true), 150);
      return () => clearTimeout(timer);
    } else {
      setShowContent(false);
    }
  }, [isOpen]);

  // Handle share to Discord
  const handleShare = async () => {
    const shareText = `🎉 I just unlocked the "${name}" badge (${rarityConfig.name})! ${earnedReason}`;

    try {
      await navigator.clipboard.writeText(shareText);
      onShare?.(shareText);

      if (typeof window !== 'undefined' && window.toast) {
        window.toast.success('Share text copied! 📋');
      }
    } catch (err) {
      console.error('Share failed:', err);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/85 backdrop-blur-md"
          onClick={onClose}
        />

        {/* Main Content */}
        <motion.div
          initial={{ opacity: 0, scale: 0.7, rotateY: -90 }}
          animate={{ opacity: 1, scale: 1, rotateY: 0 }}
          exit={{ opacity: 0, scale: 0.9, rotateY: 90 }}
          transition={{
            type: 'spring',
            stiffness: 200,
            damping: 20
          }}
          className="relative z-10 max-w-lg w-full"
        >
          {/* Close Button */}
          <motion.button
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
            onClick={onClose}
            className="absolute -top-12 right-0 p-2 text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </motion.button>

          {/* Card */}
          <div className={cn(
            'bg-gradient-to-b from-gray-900 via-gray-900 to-gray-950 rounded-3xl overflow-hidden',
            'border-2',
            rarityConfig.border,
            rarityConfig.glow
          )}>
            {/* Top Rarity Bar */}
            <motion.div
              animate={{
                opacity: [0.5, 1, 0.5]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut'
              }}
              className={cn('h-2 bg-gradient-to-r', rarityConfig.bg)}
            />

            {/* Content */}
            <div className="relative p-8">
              {/* Background Glow */}
              <motion.div
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.3, 0.5, 0.3]
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }}
                className="absolute top-1/4 left-1/2 -translate-x-1/2 w-64 h-64 rounded-full blur-3xl"
                style={{ backgroundColor: `${rarityConfig.color}20` }}
              />

              {/* Floating Particles */}
              {rarity !== 'common' && (
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                  {[...Array(Math.min(rarityConfig.particles / 10, 10))].map((_, i) => (
                    <FloatingParticle
                      key={i}
                      delay={i * 0.2}
                      duration={2 + Math.random() * 2}
                      scale={0.6 + Math.random() * 0.4}
                    />
                  ))}
                </div>
              )}

              {/* Top Section */}
              <div className="relative text-center mb-6">
                {/* Badge Icon Container */}
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{
                    type: 'spring',
                    stiffness: 200,
                    damping: 15,
                    delay: 0.2
                  }}
                  className="relative inline-block"
                >
                  {/* Outer Glow Ring */}
                  <motion.div
                    animate={{
                      scale: [1, 1.1, 1],
                      opacity: [0.5, 0.8, 0.5]
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: 'easeInOut'
                    }}
                    className={cn(
                      'absolute inset-0 rounded-full blur-2xl',
                      rarityConfig.glow
                    )}
                    style={{ backgroundColor: rarityConfig.color }}
                  />

                  {/* Badge Container */}
                  <div className={cn(
                    'relative p-8 rounded-full',
                    'bg-gradient-to-br',
                    rarityConfig.bg,
                    'ring-4',
                    rarityConfig.ring
                  )}>
                    <div className="relative">
                      <motion.div
                        animate={{
                          rotate: [0, 5, -5, 0],
                          scale: [1, 1.05, 1]
                        }}
                        transition={{
                          duration: 3,
                          repeat: Infinity,
                          ease: 'easeInOut'
                        }}
                      >
                        <IconComponent
                          className="w-24 h-24"
                          style={{ color: rarityConfig.color }}
                        />
                      </motion.div>

                      {/* Shine Effect */}
                      <div className="absolute inset-0 overflow-hidden rounded-full">
                        <ShineEffect />
                      </div>
                    </div>

                    {/* Rarity Crown for Epic/Legendary */}
                    {(rarity === 'epic' || rarity === 'legendary') && (
                      <motion.div
                        initial={{ scale: 0, y: -10 }}
                        animate={{ scale: 1, y: 0 }}
                        transition={{
                          type: 'spring',
                          stiffness: 300,
                          damping: 15,
                          delay: 0.5
                        }}
                        className="absolute -top-4 left-1/2 -translate-x-1/2"
                      >
                        <Crown
                          className="w-10 h-10"
                          style={{ color: rarityConfig.color }}
                        />
                      </motion.div>
                    )}
                  </div>

                  {/* Spinning Stars */}
                  {rarity === 'legendary' && (
                    <>
                      {[0, 120, 240].map((angle, i) => (
                        <motion.div
                          key={i}
                          animate={{
                            rotate: 360
                          }}
                          transition={{
                            duration: 8,
                            repeat: Infinity,
                            ease: 'linear',
                            delay: i * 0.2
                          }}
                          className="absolute top-1/2 left-1/2"
                          style={{
                            transformOrigin: '0 0',
                            transform: `rotate(${angle}deg) translateX(80px)`
                          }}
                        >
                          <Star className="w-6 h-6 text-yellow-400 fill-yellow-400" />
                        </motion.div>
                      ))}
                    </>
                  )}
                </motion.div>

                {/* Badge Name */}
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="mt-6 text-3xl font-bold text-white"
                >
                  {name}
                </motion.h2>

                {/* Rarity Badge */}
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{
                    type: 'spring',
                    stiffness: 400,
                    damping: 15,
                    delay: 0.7
                  }}
                  className={cn(
                    'mt-3 inline-flex items-center gap-2 px-4 py-1.5 rounded-full',
                    'bg-gradient-to-r',
                    rarityConfig.bg,
                    'border',
                    rarityConfig.border
                  )}
                >
                  <Award className="w-4 h-4" style={{ color: rarityConfig.color }} />
                  <span className={cn('font-bold text-sm', rarityConfig.text)}>
                    {rarityConfig.name}
                  </span>
                </motion.div>
              </div>

              {/* Description */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="space-y-4"
              >
                <div className="p-4 rounded-xl bg-gray-800/50 border border-gray-700">
                  <p className="text-gray-300 text-center">{description}</p>
                </div>

                {/* How it was earned */}
                <div className="flex items-center justify-center gap-2 text-sm text-gray-400">
                  <Trophy className="w-4 h-4" />
                  <span>{earnedReason}</span>
                </div>

                {/* XP Awarded */}
                {xpAwarded > 0 && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{
                      type: 'spring',
                      stiffness: 400,
                      damping: 15,
                      delay: 1
                    }}
                    className="flex items-center justify-center gap-2 p-3 rounded-xl bg-gradient-to-r from-green-500/20 to-emerald-500/10 border border-green-500/30"
                  >
                    <Zap className="w-5 h-5 text-green-400" />
                    <span className="text-lg font-bold text-green-400">
                      +{xpAwarded} XP
                    </span>
                  </motion.div>
                )}
              </motion.div>

              {/* Action Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1 }}
                className="mt-6 flex gap-3"
              >
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={onClose}
                  className="flex-1 py-3 px-4 rounded-xl bg-gray-800 text-white font-semibold hover:bg-gray-700 transition-colors"
                >
                  Close
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05, rotate: 2 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleShare}
                  className={cn(
                    'flex items-center justify-center gap-2 py-3 px-6 rounded-xl font-semibold transition-colors',
                    'bg-gradient-to-r',
                    rarityConfig.bg,
                    'border',
                    rarityConfig.border,
                    rarityConfig.text
                  )}
                >
                  <Share2 className="w-5 h-5" />
                  Share
                </motion.button>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default BadgeUnlockModal;
